import {
  RUNTIME_OFFER_PLACEMENT_DASHA_CALCULATOR_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_HI_DASHA_CALCULATOR_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";
import type { ReactNode } from "react";
import CalculatorReportBridge from "@/components/CalculatorReportBridge";
import type { KundaliResponse } from "@/lib/api/kundali";
import { formatCivilDate } from "@/lib/civil-date";
import { DASHA_DETAILS, formatNakshatra } from "./_data";

export { DashaSeoContent } from "./_seo-content";

type KundaliDasha = NonNullable<KundaliResponse["dasha"]>;
type EnginePeriod = KundaliDasha["all_mahadashas"][number];

const COPY = {
  en: {
    eyebrow: "Vimshottari Dasha",
    title: (name: string) => `Dasha periods for ${name}`,
    moonNakshatra: "Your Moon Nakshatra",
    moonIn: "Moon in",
    birthLord: "Birth Mahadasha Lord",
    currentHeading: "Your current Dasha",
    currentMahadasha: "Current Mahadasha",
    currentAntardasha: "Current Antardasha",
    sequenceHeading: "Your engine-calculated Mahadasha timeline",
    sequenceBody:
      "These period boundaries come from your birth Moon's exact sidereal longitude, not a generic planet-order lookup.",
    activeNow: "Active now",
    years: "years",
    approximate:
      "Birth time was not provided, so Moon-based period boundaries are approximate. Recalculate with an exact birth time before using a boundary date for a decision.",
    unavailable: "The Dasha engine did not return a period timeline. Please recalculate.",
  },
  hi: {
    eyebrow: "विंशोत्तरी दशा",
    title: (name: string) => `${name} की दशा अवधि`,
    moonNakshatra: "आपका चंद्र नक्षत्र",
    moonIn: "चंद्र राशि",
    birthLord: "जन्म महादशा स्वामी",
    currentHeading: "आपकी वर्तमान दशा",
    currentMahadasha: "वर्तमान महादशा",
    currentAntardasha: "वर्तमान अंतर्दशा",
    sequenceHeading: "इंजन से गणित महादशा कालक्रम",
    sequenceBody:
      "ये अवधि-सीमाएं आपके जन्म चंद्रमा के सटीक निरयण देशांतर से निकली हैं, किसी सामान्य ग्रह-क्रम सूची से नहीं।",
    activeNow: "अभी सक्रिय",
    years: "वर्ष",
    approximate:
      "जन्म समय नहीं दिया गया था, इसलिए चंद्र-आधारित अवधि सीमाएं अनुमानित हैं। किसी निर्णय में सीमा-तिथि उपयोग करने से पहले सटीक जन्म समय के साथ दोबारा गणना करें।",
    unavailable: "दशा इंजन ने अवधि-कालक्रम नहीं लौटाया। कृपया दोबारा गणना करें।",
  },
} as const;

export function DashaResult({
  result,
  defaultLanguage = "en",
  crossLinks,
}: {
  result: KundaliResponse;
  defaultLanguage?: "en" | "hi";
  crossLinks: ReactNode;
}) {
  const dasha = result.dasha;
  const copy = COPY[defaultLanguage];
  if (!dasha) return <p role="alert">{copy.unavailable}</p>;

  const sourcePath = defaultLanguage === "hi" ? "/hi/dasha-calculator" : "/dasha-calculator";
  const bridgePlacement =
    defaultLanguage === "hi"
      ? RUNTIME_OFFER_PLACEMENT_HI_DASHA_CALCULATOR_REPORT_BRIDGE
      : RUNTIME_OFFER_PLACEMENT_DASHA_CALCULATOR_REPORT_BRIDGE;

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-12">
        <DashaHeader name={String(result.birth_details.name)} language={defaultLanguage} />
        {!result.birth_time_known && <ApproximationNotice text={copy.approximate} />}
        <MoonNakshatraCard result={result} dasha={dasha} language={defaultLanguage} />
        <CurrentDashaCard dasha={dasha} language={defaultLanguage} />
        <CalculatorReportBridge
          planId="mahadasha_forecast"
          source={sourcePath}
          intent="chart_report"
          placement={bridgePlacement}
          title={
            defaultLanguage === "hi"
              ? "Mahadasha Forecast me 12-month timing dekhein"
              : "Turn this current dasha into a 12-month Mahadasha Forecast"
          }
          body={
            defaultLanguage === "hi"
              ? "Calculator vartamaan mahadasha aur antardasha dikhata hai. Forecast me transition, decision windows aur graha-specific upay aapki kundali se milte hain."
              : "The calculator shows your current mahadasha and antardasha. The forecast adds transitions, decision windows, and graha-specific upay from your chart."
          }
          cta={defaultLanguage === "hi" ? "Mahadasha Forecast paayein" : "Get Mahadasha Forecast"}
          language={defaultLanguage}
        />
        <DashaSequence dasha={dasha} language={defaultLanguage} />
        {crossLinks}
      </div>
    </div>
  );
}

function DashaHeader({ name, language }: { name: string; language: "en" | "hi" }) {
  const copy = COPY[language];
  return (
    <div className="text-center mb-8">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] mb-3 text-ish-accent">
        {copy.eyebrow}
      </p>
      <h2 className="text-3xl sm:text-4xl font-bold leading-tight font-serif text-ish-dark">
        {copy.title(name)}
      </h2>
    </div>
  );
}

function ApproximationNotice({ text }: { text: string }) {
  return (
    <p
      role="note"
      className="mb-6 rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm text-ish-dark"
    >
      {text}
    </p>
  );
}

function MoonNakshatraCard({
  result,
  dasha,
  language,
}: {
  result: KundaliResponse;
  dasha: KundaliDasha;
  language: "en" | "hi";
}) {
  const copy = COPY[language];
  return (
    <div className="card-sacred p-6 mb-6 text-center">
      <p className="text-sm font-semibold uppercase tracking-wide mb-2 text-ish-text-faint">
        {copy.moonNakshatra}
      </p>
      <p className="text-2xl font-bold font-serif text-ish-dark">
        {formatNakshatra(dasha.moon_nakshatra)}
      </p>
      <p className="text-sm mt-2 text-ish-text-muted">
        {copy.moonIn} {formatNakshatra(result.moon_rashi)}
      </p>
      <div className="mt-4 inline-block rounded-lg bg-ish-accent-light px-4 py-2 text-sm font-semibold text-ish-accent">
        {copy.birthLord}: {formatNakshatra(dasha.moon_nakshatra_lord)}
      </div>
    </div>
  );
}

function CurrentDashaCard({ dasha, language }: { dasha: KundaliDasha; language: "en" | "hi" }) {
  const copy = COPY[language];
  return (
    <section aria-label={copy.currentHeading} className="card-sacred mb-6 p-5 sm:p-6">
      <h3 className="mb-4 text-lg font-bold font-serif text-ish-dark">{copy.currentHeading}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <CurrentPeriod
          label={copy.currentMahadasha}
          period={dasha.current_mahadasha}
          language={language}
        />
        <CurrentPeriod
          label={copy.currentAntardasha}
          period={dasha.current_antardasha}
          language={language}
        />
      </div>
    </section>
  );
}

function CurrentPeriod({
  label,
  period,
  language,
}: {
  label: string;
  period: EnginePeriod;
  language: "en" | "hi";
}) {
  return (
    <div className="rounded-lg bg-ish-accent-light p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-ish-accent">{label}</p>
      <p className="mt-1 text-xl font-bold font-serif text-ish-dark">
        {formatNakshatra(period.lord)}
      </p>
      <p className="mt-1 text-xs text-ish-text-muted">
        {formatCivilDate(period.start, language)} – {formatCivilDate(period.end, language)}
      </p>
    </div>
  );
}

function DashaSequence({ dasha, language }: { dasha: KundaliDasha; language: "en" | "hi" }) {
  const copy = COPY[language];
  const current = dasha.current_mahadasha;
  return (
    <section aria-label={copy.sequenceHeading} className="card-sacred mt-6 p-5 sm:p-6">
      <h3 className="mb-2 text-lg font-bold font-serif text-ish-dark">{copy.sequenceHeading}</h3>
      <p className="mb-5 text-sm text-ish-text-muted">{copy.sequenceBody}</p>
      <div className="space-y-3">
        {dasha.all_mahadashas.map((period, index) => (
          <DashaPeriod
            key={`${period.lord}:${period.start}`}
            period={period}
            index={index}
            active={period.lord === current.lord && period.start === current.start}
            language={language}
          />
        ))}
      </div>
    </section>
  );
}

function DashaPeriod({
  period,
  index,
  active,
  language,
}: {
  period: EnginePeriod;
  index: number;
  active: boolean;
  language: "en" | "hi";
}) {
  const copy = COPY[language];
  const lord = formatNakshatra(period.lord);
  const details = DASHA_DETAILS[lord];
  return (
    <details className="group" open={active}>
      <summary className="flex cursor-pointer items-center justify-between rounded-lg p-3 transition-colors hover:bg-stone-50">
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-ish-accent-light text-xs font-bold text-ish-accent">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-ish-dark">
            {lord} Mahadasha
            {active && <span className="ml-2 text-xs">{copy.activeNow}</span>}
          </span>
        </div>
        <span className="text-right font-medium text-ish-text-faint">
          <span className="block text-xs">
            {period.years} {copy.years}
          </span>
          <span className="block text-xs">
            {formatCivilDate(period.start, language)} – {formatCivilDate(period.end, language)}
          </span>
        </span>
      </summary>
      {details && (
        <div className="pb-3 pl-14 pr-3 text-sm text-ish-text-muted">
          <p className="mb-2">{details.significations}</p>
          <p className="leading-relaxed">{details.nature}</p>
        </div>
      )}
    </details>
  );
}
